import { MetastructureField, MetastructureFieldType } from '__types__';
import {
  resourceTypeBindingsUtils,
  type ResourceTypeWorkspaceBinding
} from 'v5/api/resource-types/resource-type-bindings.api';
import { mapAssignmentsToMetastructureFields } from './mapAssignmentsToMetastructureFields';

/**
 * Append-only: workspace overlay fields that are active and not already on the
 * global metastructure are added after global fields.
 */
export function mergeWorkspaceMetastructureFields(
  globalFields: MetastructureField[],
  bindings: ResourceTypeWorkspaceBinding[],
  resourceTypeId: number,
  workspaceId: number
): MetastructureField[] {
  const binding = resourceTypeBindingsUtils.resolveBinding(
    bindings,
    resourceTypeId,
    workspaceId
  );
  const overlay = mapAssignmentsToMetastructureFields(
    binding.workspaceFields.filter(field => field.active)
  );
  if (overlay.length === 0) {
    return globalFields;
  }

  const existingCustomIds = new Set(
    globalFields
      .filter(
        (
          field
        ): field is Extract<
          MetastructureField,
          { type: MetastructureFieldType.Custom }
        > => field.type === MetastructureFieldType.Custom
      )
      .map(field => field.custom_field_definition_id)
  );
  const existingCoreNames = new Set(
    globalFields
      .filter(
        (
          field
        ): field is Extract<
          MetastructureField,
          { type: MetastructureFieldType.Core }
        > => field.type === MetastructureFieldType.Core
      )
      .map(field => field.name)
  );

  const appended = overlay.filter(field => {
    if (field.type === MetastructureFieldType.Custom) {
      return !existingCustomIds.has(field.custom_field_definition_id);
    }
    if (field.type === MetastructureFieldType.Core) {
      return !existingCoreNames.has(field.name);
    }
    return false;
  });

  return [...globalFields, ...appended];
}

export function isResourceTypeEnabledInWorkspace(
  bindings: ResourceTypeWorkspaceBinding[],
  resourceTypeId: number,
  workspaceId: number
): boolean {
  return resourceTypeBindingsUtils.isEnabledInWorkspace(
    bindings,
    resourceTypeId,
    workspaceId
  );
}
